import { Accordion } from '@iwoca/orion';
import { messages } from './BlendedRateSummary.messages';
import { FormattedMessage } from 'react-intl';
import { MoneyNoDecimals } from '../../../../../myAccount/ui/moneyNoDecimals';
import { GetDrawDownRepaymentScheduleCreditFacilityResponse } from '@iwoca/lapi-client/edge';
import { Divider, H3, Text } from '@ui';
import { LoadingAnimation } from '../../../../../myAccount/ui/loadingAnimation/LoadingAnimation';
import { Rate } from '.././../../../../myAccount/ui/rate';

export const BlendedRateSummary = ({
    topupDrawdownAmount,
    blendedRateDetails,
    isLoading
}: {
    topupDrawdownAmount: number;
    blendedRateDetails: GetDrawDownRepaymentScheduleCreditFacilityResponse['data']['summary']['blended_rate_details'];
    isLoading: boolean;
}) => {
    if (isLoading) return <LoadingAnimation data-testid="blendedRateLoading" />;

    if (!blendedRateDetails) return null;

    const {
        principal_pre_funding,
        rate_on_pre_funding_principal,
        rate_on_post_funding_principal,
        principal_post_funding,
        rate_on_funding_amount
    } = blendedRateDetails;

    if (
        rate_on_pre_funding_principal === rate_on_funding_amount ||
        rate_on_funding_amount === rate_on_post_funding_principal
    )
        return null;

    return (
        <div data-testid="blendedRateSummary">
            <H3>
                <FormattedMessage {...messages.title} />
            </H3>

            <Text className="my-s">
                <FormattedMessage {...messages.description} />
            </Text>

            <Accordion
                title={
                    <FormattedMessage
                        {...messages.accordionTitle}
                        key="descriptionTitle" // Required as otherwise react moans about not having a key. This is weird behaviour when combining Accordion and FormattedMessage
                    />
                }
                icon="questionMarkOutline"
                small
                className="mb-l box-border w-full md:w-3/4"
            >
                <Text>
                    <FormattedMessage {...messages.accordionDescription} />
                </Text>

                <ol className="space-y-l pl-l">
                    <li>
                        <FormattedMessage
                            {...messages.descriptionListItemOne}
                        />
                        <div className="font-bold">
                            <FormattedMessage
                                {...messages.formulaListItemOne}
                                values={{
                                    principal_pre_funding: (
                                        <MoneyNoDecimals
                                            value={principal_pre_funding}
                                        />
                                    ),

                                    rate_on_pre_funding_principal: (
                                        <Rate
                                            value={
                                                rate_on_pre_funding_principal
                                            }
                                        />
                                    )
                                }}
                            />
                        </div>
                    </li>
                    <li>
                        <FormattedMessage
                            {...messages.descriptionListItemTwo}
                        />
                        <div className="font-bold">
                            <FormattedMessage
                                {...messages.formulaListItemTwo}
                                values={{
                                    principal_pre_funding: (
                                        <MoneyNoDecimals
                                            value={principal_pre_funding}
                                        />
                                    ),
                                    rate_on_pre_funding_principal: (
                                        <Rate
                                            value={
                                                rate_on_pre_funding_principal
                                            }
                                        />
                                    ),
                                    newFundingAmount: (
                                        <MoneyNoDecimals
                                            value={topupDrawdownAmount}
                                        />
                                    ),
                                    rate_on_funding_amount: (
                                        <Rate value={rate_on_funding_amount} />
                                    )
                                }}
                            />
                        </div>
                    </li>
                    <li>
                        <FormattedMessage
                            {...messages.descriptionListItemThree}
                        />
                        <div className="font-bold">
                            <FormattedMessage
                                {...messages.formulaListItemThree}
                                values={{
                                    principal_pre_funding: (
                                        <MoneyNoDecimals
                                            value={principal_pre_funding}
                                        />
                                    ),
                                    rate_on_pre_funding_principal: (
                                        <Rate
                                            value={
                                                rate_on_pre_funding_principal
                                            }
                                        />
                                    ),
                                    newFundingAmount: (
                                        <MoneyNoDecimals
                                            value={topupDrawdownAmount}
                                        />
                                    ),
                                    rate_on_funding_amount: (
                                        <Rate value={rate_on_funding_amount} />
                                    )
                                }}
                            />
                        </div>
                    </li>
                </ol>
            </Accordion>

            <div className="space-y-l">
                <div className="flex items-center justify-between">
                    <FormattedMessage {...messages.existingPrincipal} />
                    <div>
                        <MoneyNoDecimals value={principal_pre_funding} />{' '}
                        <span className="font-light">
                            <FormattedMessage {...messages.at} />
                        </span>{' '}
                        <Rate value={rate_on_pre_funding_principal} />
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <FormattedMessage {...messages.newFunds} />
                    <div>
                        <MoneyNoDecimals value={topupDrawdownAmount} />{' '}
                        <span className="font-light">
                            <FormattedMessage {...messages.at} />
                        </span>{' '}
                        <Rate value={rate_on_funding_amount} />
                    </div>
                </div>

                <Divider />

                <div className="flex items-center justify-between">
                    <FormattedMessage {...messages.newLoanPrincipal} />
                    <div>
                        <MoneyNoDecimals value={principal_post_funding} />{' '}
                        <span className="font-light">
                            <FormattedMessage {...messages.at} />
                        </span>{' '}
                        <Rate value={rate_on_post_funding_principal} />
                    </div>
                </div>

                <Text className="text-structure-40">
                    <FormattedMessage {...messages.replacementText} />
                </Text>
            </div>
        </div>
    );
};
